import React, { createContext, useEffect, useState, useCallback } from "react";

import { StorageKeys } from "src/platform/constants/Storage";
import { IUser } from "src/platform/types/shared-types";
import debugLogger from "src/platform/utils/debugLogger";

import authService, { LoginCredentials } from "../services/authService";
import { orgSessionManager } from "../services/orgSessionManager";
import { clearOrganizationData } from "../utils/organizationHelpers";

interface UserContextType {
  user: IUser | null;
  token: string | null;
  setUser: (user: IUser) => void;
  setToken: (token: string) => void;
  login: (credentials: LoginCredentials) => Promise<void>;
  signUp: (userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loginWithOkta: (authState: any) => void;
  processOAuthToken: (token: string) => void;
  logout: () => void;
  loading: boolean;
  error: string | null;
  isOktaAuthenticated: boolean;
  authInProgress: boolean;
  setAuthInProgress: (status: boolean) => void;
}

export const UserContext = createContext<UserContextType>({
  user: null,
  token: null,
  setUser: () => {},
  setToken: () => {},
  login: async () => {},
  signUp: async () => {},
  loginWithOkta: () => {},
  processOAuthToken: () => {},
  logout: () => {},
  loading: false,
  error: null,
  isOktaAuthenticated: false,
  authInProgress: false,
  setAuthInProgress: () => {},
});

/**
 * User Provider Component
 * Manages authentication state using authService
 */
export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOktaAuthenticated, setIsOktaAuthenticated] = useState(false);
  const [authInProgress, setAuthInProgress] = useState(false);

  // Initialize user from authService
  useEffect(() => {
    const { user: storedUser, token: storedToken } =
      authService.initializeAuth();

    // Initialize OrgSessionManager from stored token before any component reads it
    orgSessionManager.initialize();

    if (storedUser) {
      setUser(storedUser);
    }

    if (storedToken) {
      setToken(storedToken);
    }

    const oktaAuthStatus = localStorage.getItem(StorageKeys.OKTA_AUTH);
    if (oktaAuthStatus) {
      setIsOktaAuthenticated(true);
    }

    setLoading(false);
  }, []);

  // Handle user update through authService
  const handleSetUser = useCallback((newUser: IUser) => {
    setUser(newUser);
    localStorage.setItem(StorageKeys.USER, JSON.stringify(newUser));
  }, []);

  // Handle token update through authService
  const handleSetToken = useCallback((newToken: string) => {
    setToken(newToken);
    localStorage.setItem(StorageKeys.AUTH_TOKEN, newToken);
  }, []);

  // Login with credentials using authService
  const login = useCallback(async (credentials: LoginCredentials) => {
    setAuthInProgress(true);
    setError(null);
    try {
      const result = await authService.login(credentials);
      setToken(result.token);
      if (result.user) {
        setUser(result.user);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Login failed";
      setError(errorMessage);
      debugLogger.error("Login error:", error);
      throw error;
    } finally {
      setAuthInProgress(false);
    }
  }, []);

  // Sign up with user data
  const signUp = useCallback(
    async (userData: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
    }) => {
      setAuthInProgress(true);
      setError(null);
      try {
        const result = await authService.signup(userData);
        setToken(result.token);
        if (result.user) {
          setUser(result.user);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Sign up failed";
        setError(errorMessage);
        throw error;
      } finally {
        setAuthInProgress(false);
      }
    },
    [],
  );

  // Process Okta auth state using authService
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const loginWithOkta = useCallback((authState: any) => {
    if (authState.isAuthenticated) {
      try {
        const result = authService.processOktaAuth(authState);
        setToken(result.token);
        if (result.user) {
          setUser(result.user);
        }
        setIsOktaAuthenticated(true);
      } catch (error) {
        debugLogger.error("Okta auth processing error:", error);
      }
    }
  }, []);

  // Process OAuth token using authService
  const processOAuthToken = useCallback((tokenValue: string) => {
    try {
      const result = authService.processOAuthToken(tokenValue);
      setToken(result.token);
      // User data may be loaded later or separately
    } catch (error) {
      debugLogger.error("OAuth token processing error:", error);
    }
  }, []);

  // Logout using authService
  const logout = useCallback(() => {
    authService.logout();
    clearOrganizationData();
    setUser(null);
    setToken(null);
    setError(null);
    setIsOktaAuthenticated(false);
  }, []);

  return (
    <UserContext.Provider
      value={{
        user,
        token,
        setUser: handleSetUser,
        setToken: handleSetToken,
        login,
        signUp,
        loginWithOkta,
        processOAuthToken,
        logout,
        loading,
        error,
        isOktaAuthenticated,
        authInProgress,
        setAuthInProgress,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
